import { HttpService } from 'core/services/http';

/** Legacy bad URLs where "/api/uploaded_media/" was prefixed to absolute https URLs */
export const BROKEN_UPLOAD_MEDIA_MARKERS = [
    'https://jobflowconnect.com/api/uploaded_media/https',
    '/api/uploaded_media/https',
];

/** Muted, low-chroma pairs so default covers feel calm on white UI */
const HEADER_GRADIENT_PAIRS = [
    ['#c8d4e4', '#dde5ef'],
    ['#cfd8e3', '#e4e9f0'],
    ['#d5e0db', '#e6eee9'],
    ['#dcd8e4', '#eae6f0'],
    ['#d8dce6', '#e8ebf2'],
    ['#d4dde8', '#e2eaf2'],
    ['#e0dcd8', '#edeae6'],
    ['#d0d8e2', '#dfe6ee'],
    ['#d8e0e0', '#e8eded'],
    ['#d6d2dc', '#e6e2ea'],
];

function hashSeed(seed) {
    const s = String(seed ?? '');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = s.charCodeAt(i) + ((h << 5) - h);
    }
    return Math.abs(h);
}

/** CSS linear-gradient for profile/cover when user has no header_image */
export function defaultProfileHeaderGradient(seed) {
    const [a, b] = HEADER_GRADIENT_PAIRS[hashSeed(seed) % HEADER_GRADIENT_PAIRS.length];
    return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

export function normalizeProfileMediaUrl(url) {
    if (!url) return null;
    let out = url;
    if (out.includes('%3A') || out.includes('%2F')) {
        try {
            out = decodeURIComponent(out);
        } catch {
            /* keep */
        }
    }
    for (const m of BROKEN_UPLOAD_MEDIA_MARKERS) {
        if (out.includes(m)) {
            out = out.replace(m, 'https');
        }
    }
    if (out.startsWith('http')) return out;
    const base = HttpService.SiteBase || '';
    if (out.startsWith('/')) return base + out;
    return `${base}/${out}`;
}

/** Muted fills for letter avatars (aligned with candidate cards) */
const AVATAR_FALLBACK_COLORS = [
    '#6b7c8f', '#7d8c9c', '#8b7d9a', '#7a8f86',
    '#8f7b7b', '#758794', '#857b8f', '#7d9189',
];

export function nameToAvatarColor(name) {
    const s = String(name ?? '');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = s.charCodeAt(i) + ((h << 5) - h);
    }
    return AVATAR_FALLBACK_COLORS[Math.abs(h) % AVATAR_FALLBACK_COLORS.length];
}

/**
 * Display name for avatar fallback (first + last, else email local-part, else user fields).
 * @param {object} profile - profile object with first_name, last_name, email
 * @param {{ username?: string, email?: string }} [fallbackUser] - optional user row when profile is sparse
 */
export function getProfileDisplayName(profile, fallbackUser = null) {
    const fromProfile = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
    if (fromProfile) return fromProfile;
    const em = profile?.email?.trim();
    if (em && em.includes('@')) return em.split('@')[0];
    if (fallbackUser?.username) return String(fallbackUser.username);
    if (fallbackUser?.email?.trim()) {
        const fe = fallbackUser.email.trim();
        if (fe.includes('@')) return fe.split('@')[0];
        return fe;
    }
    return 'Member';
}

/** Single initial letter for MUI Avatar children (same idea as candidate people cards). */
export function getProfileAvatarLetter(profile, fallbackUser = null) {
    const name = getProfileDisplayName(profile, fallbackUser);
    const c = name.trim().charAt(0);
    return c && /\S/.test(c) ? c.toUpperCase() : '?';
}

export function getProfileAvatarUrl(profile) {
    const raw = profile?.image || profile?.avatar;
    return normalizeProfileMediaUrl(raw);
}

export function getProfileHeaderUrl(profile) {
    return normalizeProfileMediaUrl(profile?.header_image);
}

/** Strip proficiency / suffix after spaced dash (e.g. "React - Intermediate" → "React") */
export function stripSkillTitleSuffix(text) {
    if (!text || typeof text !== 'string') return '';
    const m = text.match(/^(.+?)\s[-–—]\s.+$/);
    return (m ? m[1] : text).trim();
}

export function looksLikeEmailAddress(text) {
    if (!text || typeof text !== 'string') return false;
    const t = text.trim();
    if (!t.includes('@') || /\s/.test(t)) return false;
    const parts = t.split('@');
    if (parts.length !== 2) return false;
    const [local, domain] = parts;
    if (!local || !domain) return false;
    return domain.includes('.');
}

/**
 * Display label for user skills: prefer catalog name, else title without " - level" tail.
 */
export function formatSkillLabelForDisplay(userSkill) {
    const catalog = userSkill?.skill?.title?.trim();
    const raw = catalog || (userSkill?.title || '').trim();
    const out = stripSkillTitleSuffix(raw);
    if (looksLikeEmailAddress(out)) return '';
    return out;
}
